import React from 'react';
import { Container, Typography, Box } from '@mui/material';
import { useTranslation } from '../hooks/useTranslation';

function shown(translate: (key: string) => string, key: string, fallback: string): string {
  const value = translate(key);
  return value === key ? fallback : value;
}

const TermsOfService: React.FC = () => {
  const { t } = useTranslation();

  return (
    <div className="min-h-screen bg-white">
      <Container maxWidth="lg" className="py-8 px-8">
        <Typography 
          variant="h4" 
          component="h1" 
          className="mb-6 text-left text-black"
        >
          Terms of Service
        </Typography>
        
        <Typography variant="body2" className="mb-8 text-left text-black">
          Last updated: {new Date().toLocaleDateString()}
        </Typography>

        <Box className="space-y-6">
          <section>
            <Typography variant="h5" component="h2" className="mb-4 text-black">
              1. Agreement to Terms
            </Typography>
            <Typography variant="body1" className="mb-4 text-black leading-relaxed">
              These Terms of Service ("Terms") constitute a legally binding agreement made between you, 
              whether personally or on behalf of an entity ("you") and SonicMakers Ltd. ("Company", "we", 
              "us", or "our"), concerning your access to and use of the sonicmakers.com website as well as any 
              other media form, media channel, mobile website or mobile application related, linked, or 
              otherwise connected thereto (collectively, the "Site").
            </Typography>
            <Typography variant="body1" className="text-black leading-relaxed">
              You agree that by accessing the Site, you have read, understood, and agreed to be bound by 
              all of these Terms. IF YOU DO NOT AGREE WITH ALL OF THESE TERMS, THEN YOU ARE EXPRESSLY 
              PROHIBITED FROM USING THE SITE AND YOU MUST DISCONTINUE USE IMMEDIATELY.
            </Typography>
          </section>



          <section>
            <Typography variant="h5" component="h2" className="mb-4 text-black">
              2. Intellectual Property Rights
            </Typography>
            <Typography variant="body1" className="mb-4 text-black leading-relaxed">
              Unless otherwise indicated, the Site is our proprietary property and all source code, 
              databases, functionality, software, website designs, audio, video, text, photographs, 
              and graphics on the Site (collectively, the "Content") and the trademarks, service marks, 
              and logos contained therein (the "Marks") are owned or controlled by us or licensed to us.
            </Typography>
            <Typography variant="body1" className="text-black leading-relaxed">
              You are granted a limited license only for purposes of viewing the material contained on 
              this Site. All VST plugins and audio content are protected by copyright and international 
              treaty provisions. Unauthorized copying, distribution, modification, public display, or 
              public performance is strictly prohibited.
            </Typography>
          </section>



          <section>
            <Typography variant="h5" component="h2" className="mb-4 text-black">
              3. User Registration and Account Security
            </Typography>
            <Typography variant="body1" className="mb-4 text-black leading-relaxed">
              You may be required to register with the Site. You agree to keep your password confidential 
              and will be responsible for all use of your account and password. We reserve the right to 
              remove, reclaim, or change a username you select if we determine, in our sole discretion, 
              that such username is inappropriate, obscene, or otherwise objectionable.
            </Typography>
            <Typography variant="body1" className="text-black leading-relaxed">
              You are responsible for safeguarding the password and all activities that occur under your 
              account. You must notify us immediately upon becoming aware of any breach of security or 
              unauthorized use of your account.
            </Typography>
          </section>



          <section>
            <Typography variant="h5" component="h2" className="mb-4 text-black">
              4. Purchases and Payment Terms
            </Typography>
            <Typography variant="body1" className="mb-4 text-black leading-relaxed">
              All purchases through our Site are subject to these Terms. We reserve the right to refuse 
              any order you place with us. We may, in our sole discretion, limit or cancel quantities 
              purchased per person, per household or per order.
            </Typography>
            <Typography variant="body1" className="mb-4 text-black leading-relaxed">
              All payments are processed securely through Stripe. We do not store your payment information. 
              Prices are subject to change without notice. All sales are final unless otherwise stated in 
              our refund policy.
            </Typography>
            <Typography variant="body1" className="text-black leading-relaxed">
              Upon successful payment, you will receive immediate access to download your purchased VST 
              plugins and audio content. Download links are valid for a limited time and number of attempts 
              as specified during purchase.
            </Typography>
          </section>



          <section>
            <Typography variant="h5" component="h2" className="mb-4 text-black">
              5. Refund Policy
            </Typography>
            <Typography variant="body1" className="mb-4 text-black leading-relaxed">
              {shown(t, 'terms.refund_intro', 'Due to the digital nature of our products, all sales are generally final. However, we may offer refunds at our sole discretion in the following circumstances:')}
            </Typography>
            <ul className="list-disc pl-6 mb-4 text-black space-y-2">
              <li>{shown(t, 'terms.refund_technical', 'Technical issues that prevent download or proper functioning of the product')}</li>
              <li>{shown(t, 'terms.refund_duplicate', 'Duplicate purchases made within 24 hours')}</li>
              <li>{shown(t, 'terms.refund_different', 'Products that are materially different from their description')}</li>
            </ul>
            <Typography variant="body1" className="text-black leading-relaxed">
              {shown(t, 'terms.refund_contact', 'Refund requests must be made within 30 days of purchase. To request a refund, contact us at support@sonicmakers.com with your order details.')}
            </Typography>
          </section>



          <section>
            <Typography variant="h5" component="h2" className="mb-4 text-black">
              6. License to Use Products
            </Typography>
            <Typography variant="body1" className="mb-4 text-black leading-relaxed">
              Upon purchase, you are granted a non-exclusive, non-transferable license to use the VST 
              plugins and audio content for personal and commercial music production. This license includes:
            </Typography>
            <ul className="list-disc pl-6 mb-4 text-black space-y-2">
              <li>Use in your own musical compositions and productions</li>
              <li>Commercial release of music created using our products</li>
              <li>Installation on up to 3 computers you own or control</li>
            </ul>
            <Typography variant="body1" className="mb-4 text-black leading-relaxed">
              You may NOT:
            </Typography>
            <ul className="list-disc pl-6 mb-4 text-black space-y-2">
              <li>Share, redistribute, or resell our products</li>
              <li>Extract or isolate samples for standalone distribution</li>
              <li>Reverse engineer or decompile the software</li>
              <li>Remove or alter copyright notices</li>
            </ul>
          </section>



          <section>
            <Typography variant="h5" component="h2" className="mb-4 text-black">
              7. Privacy and Data Protection
            </Typography>
            <Typography variant="body1" className="text-black leading-relaxed">
              Your privacy is important to us. Our collection and use of personal information is governed 
              by our Privacy Policy, which is incorporated into these Terms by reference. By using our Site, 
              you consent to the collection and use of your information as outlined in our Privacy Policy.
            </Typography>
          </section>



          <section>
            <Typography variant="h5" component="h2" className="mb-4 text-black">
              8. Prohibited Uses
            </Typography>
            <Typography variant="body1" className="mb-4 text-black leading-relaxed">
              You may not use our Site:
            </Typography>
            <ul className="list-disc pl-6 mb-4 text-black space-y-2">
              <li>For any unlawful purpose or to solicit others to perform unlawful acts</li>
              <li>To violate any international, federal, provincial or state regulations, rules, laws, or local ordinances</li>
              <li>To infringe upon or violate our intellectual property rights or the intellectual property rights of others</li>
              <li>To harass, abuse, insult, harm, defame, slander, disparage, intimidate, or discriminate</li>
              <li>To submit false or misleading information</li>
              <li>To upload or transmit viruses or any other type of malicious code</li>
              <li>To spam, phish, pharm, pretext, spider, crawl, or scrape</li>
              <li>For any obscene or immoral purpose</li>
            </ul>
          </section>



          <section>
            <Typography variant="h5" component="h2" className="mb-4 text-black">
              9. Disclaimer of Warranties
            </Typography>
            <Typography variant="body1" className="text-black leading-relaxed">
              THE INFORMATION ON THIS SITE IS PROVIDED ON AN "AS-IS" BASIS. TO THE FULLEST EXTENT 
              PERMITTED BY LAW, SONICMAKERS LTD. EXCLUDES ALL REPRESENTATIONS, WARRANTIES, CONDITIONS 
              AND TERMS (EXPRESS OR IMPLIED) OTHER THAN THOSE EXPRESSLY SET OUT IN THE TERMS.
            </Typography>
          </section>



          <section>
            <Typography variant="h5" component="h2" className="mb-4 text-black">
              10. Limitation of Liability
            </Typography>
            <Typography variant="body1" className="text-black leading-relaxed">
              IN NO EVENT SHALL SONICMAKERS LTD., ITS OFFICERS, DIRECTORS, EMPLOYEES, OR AGENTS, BE 
              LIABLE TO YOU FOR ANY DIRECT, INDIRECT, INCIDENTAL, SPECIAL, PUNITIVE, OR CONSEQUENTIAL 
              DAMAGES WHATSOEVER RESULTING FROM ANY DAMAGES OR LOSS ARISING OUT OF YOUR USE OF THIS 
              SITE OR THE PRODUCTS OBTAINED THROUGH THIS SITE.
            </Typography>
          </section>



          <section>
            <Typography variant="h5" component="h2" className="mb-4 text-black">
              11. Governing Law
            </Typography>
            <Typography variant="body1" className="text-black leading-relaxed">
              These Terms shall be interpreted and governed by the laws of the United Kingdom. Any 
              disputes arising from these Terms or your use of the Site shall be subject to the 
              exclusive jurisdiction of the courts of the United Kingdom.
            </Typography>
          </section>



          <section>
            <Typography variant="h5" component="h2" className="mb-4 text-black">
              12. Changes to Terms
            </Typography>
            <Typography variant="body1" className="text-black leading-relaxed">
              We reserve the right, in our sole discretion, to make changes or modifications to these 
              Terms at any time and for any reason. We will alert you about any changes by updating 
              the "Last updated" date of these Terms, and you waive any right to receive specific 
              notice of each such change.
            </Typography>
          </section>



          <section>
            <Typography variant="h5" component="h2" className="mb-4 text-black">
              13. Contact Information
            </Typography>
            <Typography variant="body1" className="text-black leading-relaxed">
              If you have any questions or comments about these Terms, please contact us at:
            </Typography>
            <Box className="mt-4  bg-white ">
              <Typography variant="body1" className="text-black">
                <strong>SonicMakers Ltd.</strong><br />
                Email: legal@sonicmakers.com<br />
                Website: https://sonicmakers.com
              </Typography>
            </Box>
          </section>
        </Box>
      </Container>
    </div>
  );
};

export default TermsOfService;